/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 27/09/22
 */

import useWorkplanVersion from '@/components/contexts/VersionContext/useWorkplanVersion';
import { Tile } from '@/components/Tile';
import { Assignment } from '@/wpt-lib/interfaces/Assignment';
import { isWorkplanCompleted, isWorkplanReadonly } from '@/utils/isWorkplanReadonly';
import React from 'react';
import { MeetingDatesTile } from '@/components/pages/dashboard/tiles/MeetingDatesTile/MeetingDatesTile';
import { Box, GridItem, Heading } from '@chakra-ui/react';
import { useWorkplanCategories } from '@/components/contexts/WorkplanContext/useWorkplanCategories';
import useNavigateOptions from '@/components/contexts/NaviateContext/useNavigateOptions';
import ProbationTile from '@/components/pages/dashboard/tiles/ProbationTile/ProbationTile';
import InfoTooltip from '@/components/Tooltip/InfoTooltip';
import ReviewCommentTile from '@/components/pages/workplan/comment/ReviewCommentTile';
import WorkplanVersionStateDisplay from '@/components/contexts/VersionContext/WorkplanVersionStateDisplay';
import SupportingDocumentsTile from '@/components/pages/dashboard/tiles/SupportingDocumentsTile/SupportingDocumentsTile';
import FocusHeader from '@/components/FocusHeader/FocusHeader';

interface ReviewTileProps {
    assignment: Assignment;
    readonly?: boolean;
}

const ReviewTile = ({ assignment, readonly }: ReviewTileProps) => {
    const numCategories = useWorkplanCategories().length;
    const { supervisor, admin } = useNavigateOptions();

    const { current, loading, error } = useWorkplanVersion();

    const helpTextValue =
        'This section allows you to provide an overall summary comment on your workplan, regarding progress against your goals.\n You can attach any supporting information you would like your supervisor to consider as part of your end of year workplan review. This may include: snapshots from Research Master, SFS scores, PEP reports or screenshots of other systems or information.\n You should record the dates you met with your supervisor as part of the workplan review process.';

    return (
        <GridItem colSpan={2} role="landmark">
            <Heading as="h2" size={'md'} color={'black'} key={`heading-Review-${4}`} mb="1rem">
                {numCategories + 1}. Review
                <InfoTooltip tooltipLabel={'Review tooltip'} tooltipInfoText={helpTextValue} />
            </Heading>
            <Tile
                colspan={2}
                heading={
                    <FocusHeader as="h3" id="Overview-Heading" size={'small'} textColor="blue.500">
                        Comments and Updates
                    </FocusHeader>
                }
            >
                <WorkplanVersionStateDisplay
                    render={([workplan, workplanVersion]) => {
                        const isReadonly = !current || isWorkplanReadonly(workplan, supervisor || admin) || !!readonly;
                        return (
                            <Box>
                                <ReviewCommentTile
                                    isReadonly={isReadonly}
                                    currentAssignment={assignment}
                                    workplan={workplan}
                                    workplanVersion={workplanVersion}
                                />
                                <MeetingDatesTile
                                    isReadonly={isReadonly}
                                    currentAssignment={assignment}
                                    baseApiUrl={`workplan/${workplan.id}`}
                                    workplan={workplan}
                                    workplanVersion={workplanVersion}
                                    loading={loading}
                                    error={error}
                                />
                                <SupportingDocumentsTile
                                    isReadonly={
                                        !current ||
                                        (!admin && isWorkplanReadonly(workplan, supervisor || admin)) ||
                                        isWorkplanCompleted(workplan) ||
                                        !!readonly
                                    }
                                    currentAssignment={assignment}
                                    baseApiUrl={`workplan/${workplan.id}`}
                                    version={workplanVersion.version}
                                    workplan={workplan}
                                    loading={loading}
                                />
                                <ProbationTile readonly={isReadonly} currentAssignment={assignment} />
                            </Box>
                        );
                    }}
                />
            </Tile>
        </GridItem>
    );
};

export default ReviewTile;
